<template>
    <standard-dialog :render='!!mineAreaId' :value='show' :max-width='800' persistent>
        <template v-slot:title>
            {{ $t('shift-schedule-editor.shift-schedule') }}
        </template>
        <template v-slot:content>
            <v-container fluid v-if='schedule'>
                <v-alert type='info' dense outlined class='mb-4'>
                    {{ $t('shift-schedule-editor.each-day-must-have-shifts-that-total-exactly-1440-minutes-24-hours') }}
                </v-alert>
                <v-expansion-panels accordion multiple :value='allPanels'>
                    <v-expansion-panel v-for='day in schedule' :key='day.dayOfWeek'>
                        <v-expansion-panel-header>
                            <span>
                                <strong>{{ dayName(day.dayOfWeek) }}</strong>
                                <v-chip x-small class='ml-3' :color='dayValid(day) ? "success" : "error"'>
                                    {{ dayTotal(day) }} / 1440 min
                                </v-chip>
                            </span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-simple-table dense>
                                <thead>
                                    <tr>
                                        <th class='text-left' style='width:50px'>#</th>
                                        <th class='text-left'>{{ $t('shift-schedule-editor.shift-name') }}</th>
                                        <th class='text-left' style='width:180px'>{{ $t('shift-schedule-editor.duration-minutes') }}</th>
                                        <th style='width:50px'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for='(slot, idx) in day.slots' :key='idx'>
                                        <td>{{ idx + 1 }}</td>
                                        <td>
                                            <v-text-field
                                                dense
                                                hide-details
                                                v-model='slot.name'
                                                :rules='[$rules.required(), $rules.maxLength(100)]'
                                                class='my-1'
                                            ></v-text-field>
                                        </td>
                                        <td>
                                            <v-text-field
                                                dense
                                                hide-details
                                                type='number'
                                                v-model.number='slot.durationMinutes'
                                                :rules='[$rules.min(1)]'
                                                class='my-1'
                                            ></v-text-field>
                                        </td>
                                        <td>
                                            <v-btn icon x-small @click='removeSlot(day, idx)' :disabled='day.slots.length <= 1'>
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                            <div class='mt-2'>
                                <v-btn small text color='secondary' @click='addSlot(day)'>
                                    <v-icon left small>mdi-plus</v-icon>
                                    {{ $t('shift-schedule-editor.add-shift') }}
                                </v-btn>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-container>
            <v-container v-else-if='loading'>
                <v-progress-linear indeterminate></v-progress-linear>
            </v-container>
        </template>
        <template v-slot:actions>
            <v-spacer></v-spacer>
            <v-btn color='gray' text @click='close'>{{ $t('shared.button.cancel') }}</v-btn>
            <v-btn color='primary' text @click='save' :disabled='!allDaysValid' :loading="$wait.is('saving schedule')">{{ $t('shared.button.save') }}</v-btn>
        </template>
        <template v-slot:offline-actions>
            <v-spacer></v-spacer>
            <v-btn color='gray' text @click='close'>{{ $t('shared.button.cancel') }}</v-btn>
            <offline-btn text></offline-btn>
        </template>
    </standard-dialog>
</template>

<script>
import MineAreas from '@/lib/data/MineAreas';
import StandardDialog from '@/components/Shared/Common/StandardDialog.vue';
import { EventBus, Events } from '@/lib/EventBus';

const DAYS_OF_WEEK = [
    { id: 0, name: 'Sunday' },
    { id: 1, name: 'Monday' },
    { id: 2, name: 'Tuesday' },
    { id: 3, name: 'Wednesday' },
    { id: 4, name: 'Thursday' },
    { id: 5, name: 'Friday' },
    { id: 6, name: 'Saturday' }
];

export default {
    components: { StandardDialog },
    data() {
        return {
            show: false,
            loading: false,
            mineAreaId: null,
            schedule: null
        };
    },
    computed: {
        allPanels() {
            return this.schedule ? this.schedule.map((_, i) => i) : [];
        },
        allDaysValid() {
            if (!this.schedule) return false;
            return this.schedule.every(d => this.dayValid(d));
        }
    },
    methods: {
        async open(mineAreaId) {
            this.mineAreaId = mineAreaId;
            this.show = true;
            this.loading = true;
            try {
                const result = await MineAreas.getShiftSchedules(mineAreaId);
                const active = result && result.schedules
                    ? result.schedules.find(s => s.isActive)
                    : null;

                if (active && active.days.length > 0) {
                    // Deep copy so edits don't mutate the cache
                    this.schedule = DAYS_OF_WEEK.map(d => {
                        const existing = active.days.find(ad => ad.dayOfWeek === d.id);
                        return {
                            dayOfWeek: d.id,
                            slots: existing
                                ? existing.slots.map(s => ({ ...s }))
                                : [{ shiftNumber: 1, name: '', durationMinutes: 720 }]
                        };
                    });
                } else {
                    this.schedule = DAYS_OF_WEEK.map(d => ({
                        dayOfWeek: d.id,
                        slots: [{ shiftNumber: 1, name: '', durationMinutes: 1440 }]
                    }));
                }
            } finally {
                this.loading = false;
            }
        },
        close() {
            this.show = false;
            this.mineAreaId = null;
            this.schedule = null;
        },
        dayName(dayOfWeek) {
            return DAYS_OF_WEEK.find(d => d.id === dayOfWeek)?.name ?? dayOfWeek;
        },
        dayTotal(day) {
            return day.slots.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
        },
        dayValid(day) {
            return this.dayTotal(day) === 1440
                && day.slots.every(s => s.name && s.name.trim().length > 0 && s.durationMinutes > 0);
        },
        addSlot(day) {
            const nextNumber = day.slots.length + 1;
            day.slots.push({ shiftNumber: nextNumber, name: '', durationMinutes: 0 });
        },
        removeSlot(day, idx) {
            day.slots.splice(idx, 1);
            day.slots.forEach((s, i) => { s.shiftNumber = i + 1; });
        },
        async save() {
            if (!this.allDaysValid) return;

            this.$wait.start('saving schedule');
            try {
                await MineAreas.setShiftSchedule(this.mineAreaId, {
                    _type: 'SetMineAreaShiftScheduleCommand',
                    mineAreaId: this.mineAreaId,
                    days: this.schedule.map(d => ({
                        dayOfWeek: d.dayOfWeek,
                        slots: d.slots.map((s, i) => ({
                            shiftNumber: i + 1,
                            name: s.name,
                            durationMinutes: s.durationMinutes
                        }))
                    }))
                });
                EventBus.$emit(Events.ToastSuccess, this.$t('shift-schedule-editor.shift-schedule-saved'));
                this.close();
            } catch (e) {
                EventBus.$emit(Events.ToastError, this.$t('shift-schedule-editor.failed-to-save-shift-schedule'));
            } finally {
                this.$wait.end('saving schedule');
            }
        }
    }
};
</script>
